/**
 * SpeedTestChart.tsx
 *
 * Throughput-over-time line chart for a finished Speed Test. Plots the
 * download and upload sample series (emitted by the edge in `speedResult`)
 * on a shared time axis, with optional reference lines for the peak and
 * steady-state download. Renders nothing when there are no samples (older
 * executions / pre-v4.4.0 modems).
 */

import React from 'react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';

export interface SpeedSamplePoint {
  /** Seconds since run start. */
  t: number;
  mbps: number;
}

interface SpeedTestChartProps {
  downloadSamples?: SpeedSamplePoint[];
  uploadSamples?: SpeedSamplePoint[];
  peakDownloadMbps?: number;
  steadyDownloadMbps?: number;
  height?: number;
}

const DOWNLOAD_COLOR = '#3b82f6'; // blue
const UPLOAD_COLOR = '#10b981'; // green

const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded border border-border bg-card p-2 shadow-lg">
      <p className="mb-1 text-xs font-medium text-foreground">{Number(label).toFixed(1)}s</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-2 text-xs">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-muted-foreground">{entry.name}:</span>
          <span className="font-semibold text-foreground">
            {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value} Mbps
          </span>
        </div>
      ))}
    </div>
  );
};

export const SpeedTestChart: React.FC<SpeedTestChartProps> = ({
  downloadSamples,
  uploadSamples,
  peakDownloadMbps,
  steadyDownloadMbps,
  height = 200,
}) => {
  // Merge both phases onto one time axis. Download runs first, upload second,
  // so the series occupy disjoint time windows — connectNulls={false} keeps
  // each line confined to its own phase rather than bridging the gap.
  const data = React.useMemo(() => {
    const byT = new Map<number, { t: number; download?: number; upload?: number }>();
    for (const s of downloadSamples ?? []) {
      const row = byT.get(s.t) ?? { t: s.t };
      row.download = s.mbps;
      byT.set(s.t, row);
    }
    for (const s of uploadSamples ?? []) {
      const row = byT.get(s.t) ?? { t: s.t };
      row.upload = s.mbps;
      byT.set(s.t, row);
    }
    return Array.from(byT.values()).sort((a, b) => a.t - b.t);
  }, [downloadSamples, uploadSamples]);

  if (data.length < 2) return null;

  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="mb-2 text-xs font-medium text-muted-foreground">Throughput over time</div>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsLineChart data={data} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
          {/* Theme CSS vars are raw HSL channels — must be wrapped in hsl(). */}
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} />
          <XAxis
            dataKey="t"
            type="number"
            domain={['dataMin', 'dataMax']}
            stroke="hsl(var(--muted-foreground))"
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
            tickFormatter={(v: number) => `${Math.round(v)}s`}
            tickLine={{ stroke: 'hsl(var(--border))' }}
            axisLine={{ stroke: 'hsl(var(--border))' }}
          />
          <YAxis
            stroke="hsl(var(--muted-foreground))"
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
            tickLine={{ stroke: 'hsl(var(--border))' }}
            axisLine={{ stroke: 'hsl(var(--border))' }}
            width={40}
            label={{ value: 'Mbps', angle: -90, position: 'insideLeft', fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ paddingTop: '8px', fontSize: '12px' }} />
          {typeof peakDownloadMbps === 'number' && (
            <ReferenceLine
              y={peakDownloadMbps}
              stroke={DOWNLOAD_COLOR}
              strokeDasharray="4 4"
              strokeOpacity={0.5}
              label={{ value: `peak ${peakDownloadMbps.toFixed(0)}`, position: 'right', fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
            />
          )}
          {typeof steadyDownloadMbps === 'number' && (
            <ReferenceLine
              y={steadyDownloadMbps}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="2 4"
              strokeOpacity={0.5}
              label={{ value: `steady ${steadyDownloadMbps.toFixed(0)}`, position: 'right', fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
            />
          )}
          <Line type="monotone" dataKey="download" name="Download" stroke={DOWNLOAD_COLOR} strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false} />
          <Line type="monotone" dataKey="upload" name="Upload" stroke={UPLOAD_COLOR} strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false} />
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
};
